import ExcelJS from 'exceljs';
import { getCPM } from './excelService';
import type { Regiao } from './excelService';
import { FormData } from '../types/FormData';

// Função utilitária para cabeçalho dinâmico especial
function getColunaEspecialHeader(formato: string, tipoCompra: string): string {
  const formatoLower = (formato || '').toLowerCase();
  const compraLower = (tipoCompra || '').toLowerCase();
  if ([
    'ctv', 'ott', 'audio', 'display', 'native', 'in game'
  ].includes(formatoLower) && compraLower === 'cpm') {
    return 'Impressões';
  }
  if (formatoLower === 'whatsapp') {
    return 'Disparos';
  }
  if (formatoLower === 'push notification' && compraLower === 'cpe') {
    return 'Envios';
  }
  if ((formatoLower === 'push notification' && compraLower === 'cpc') ||
      (formatoLower === 'web push' && compraLower === 'cpc') ||
      (formatoLower === 'native' && compraLower === 'cpc')) {
    return 'Cliques';
  }
  if (formatoLower === 'alto impacto') {
    if (compraLower === 'cpv') {
      return 'Views';
    }
    return 'Impressões';
  }
  return 'Impressões'; // fallback padrão
}

// Função para formatar número com separador de milhar
function formatNumberPtBr(value: number): string {
  return value.toLocaleString('pt-BR');
}

// Função utilitária para remover acentos e normalizar string
function normalizeStr(str: string = ''): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export const generateExcelFile = async (formData: FormData): Promise<Blob> => {
  try {
    console.log('=== Iniciando geração da planilha ===');
    console.log('Dados recebidos:', formData);

    // Criar um novo workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Planejamento');

    // Limpar toda a planilha com bordas brancas
    const totalRows = 1000; // Aumentado para 1000 linhas
    const columns = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
    
    for (let row = 1; row <= totalRows; row++) {
      for (const col of columns) {
        const cell = worksheet.getCell(`${col}${row}`);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' }
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        };
      }
    }

    // Definir larguras das colunas
    worksheet.getColumn('A').width = 2.33;
    worksheet.getColumn('B').width = 53.75;
    worksheet.getColumn('C').width = 61;
    worksheet.getColumn('D').width = 25;
    worksheet.getColumn('E').width = 25;
    worksheet.getColumn('F').width = 25;
    worksheet.getColumn('G').width = 25;
    worksheet.getColumn('H').width = 25;
    worksheet.getColumn('I').width = 25;
    worksheet.getColumn('J').width = 25;
    worksheet.getColumn('K').width = 25;
    worksheet.getColumn('L').width = 25;
      worksheet.getColumn('M').width = formData.comissao ? 25 : 3;
  worksheet.getColumn('N').width = formData.comissao ? 3 : 25;
    worksheet.getColumn('O').width = 25;
    worksheet.getColumn('P').width = 25;
    worksheet.getColumn('Q').width = 25;

    // Estilos padrão
    const estilos = {
      cabecalhoPrincipal: {
        font: {
          name: 'Roboto',
          size: 12,
          bold: true,
          color: { argb: 'FFFFFFFF' }
        },
        fill: {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FF400047' }
        },
        alignment: {
          vertical: 'middle' as const,
          horizontal: 'center' as const,
          wrapText: true
        },
        border: {
          top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        }
      },
      dadosNormais: {
        font: {
          name: 'Roboto',
          size: 12,
          color: { argb: 'FF000000' }
        },
        alignment: {
          vertical: 'middle' as const,
          horizontal: 'center' as const,
          wrapText: true
        },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      brandSafety: {
        font: {
          name: 'Roboto',
          size: 12,
          color: { argb: 'FF000000' }
        },
        fill: {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FFE0E0E0' }
        },
        alignment: {
          vertical: 'middle' as const,
          horizontal: 'left' as const,
          wrapText: true
        }
      }
    };

    // Cabeçalho inicial (ANUNCIANTE, PRODUTO, etc)
    const cabecalhoInicial = [
      { celula: 'B5', valor: 'ANUNCIANTE' },
      { celula: 'B6', valor: 'PRODUTO' },
      { celula: 'B7', valor: 'PERÍODO' },
      { celula: 'B8', valor: 'VALOR' }
    ];

    cabecalhoInicial.forEach(item => {
      const cell = worksheet.getCell(item.celula);
      cell.value = item.valor;
      Object.assign(cell, estilos.cabecalhoPrincipal);
    });

    // Preencher dados do cabeçalho
    worksheet.getCell('C5').value = formData.anunciante;
    worksheet.getCell('C6').value = formData.produto;
    worksheet.getCell('C7').value = `${formatarData(formData.dataInicio)} à ${formatarData(formData.dataFim)}`;
    
    // Calcular o valor total somando todos os valores de todas as configurações
    let valorTotalFormatos = 0;
    let valorMinimoMensal = 0;

    // Somar valores de todas as configurações de Programática Open
    if (formData.configuracoesProgramaticaOpen && formData.configuracoesProgramaticaOpen.length > 0) {
      for (const configuracao of formData.configuracoesProgramaticaOpen) {
        if (configuracao.valorPorFormato) {
          for (const [formato, valor] of Object.entries(configuracao.valorPorFormato)) {
            if (valor && typeof valor === 'string') {
              // Converter valor de centavos para reais
              const valorLimpo = (parseFloat(valor) / 100) || 0;
              valorTotalFormatos += valorLimpo;
              console.log(`Configuração Open - Formato: ${formato}, Valor: ${valor}, Valor limpo: ${valorLimpo}`);
            }
          }
        }
      }
    }

    // Somar valores mínimos mensais de todas as configurações especiais
    if (formData.configuracoesProgramaticaEspecial && formData.configuracoesProgramaticaEspecial.length > 0) {
      for (const configEspecial of formData.configuracoesProgramaticaEspecial) {
        if (configEspecial.formData?.valorMinimoMensal) {
          let valorLimpo = configEspecial.formData.valorMinimoMensal.replace(/R\$\s?/g, '').trim();
      valorLimpo = valorLimpo.replace(/\./g, '').replace(',', '.');
          const valor = parseFloat(valorLimpo) || 0;
          valorMinimoMensal += valor;
          console.log(`Configuração Especial - Valor mínimo mensal: ${configEspecial.formData.valorMinimoMensal}, Valor limpo: ${valor}`);
        }
      }
    }

    const valorTotal = valorTotalFormatos + valorMinimoMensal;
    console.log('VALOR QUE VAI PARA C8 (Valor):', valorTotal);
    worksheet.getCell('C8').value = valorTotal;
    worksheet.getCell('C8').numFmt = 'R$ #,##0.00';
    
    ['C5', 'C6', 'C7', 'C8'].forEach(celula => {
      const cell = worksheet.getCell(celula);
      Object.assign(cell, estilos.dadosNormais);
      if (celula === 'C8') {
        cell.numFmt = 'R$ #,##0.00';
      }
    });

    // Definir estrutura dos formatos
    const formatosEstrutura = {
      'Display IAB': { inicio: 11, fim: 18, titulo: 'PROGRAMÁTICA DISPLAY' },
      'Native': { inicio: 20, fim: 27, titulo: 'PROGRAMÁTICA NATIVE' },
      'Vídeo Open': { inicio: 29, fim: 36, titulo: 'PROGRAMÁTICA VÍDEO' },
      'Youtube': { inicio: 38, fim: 45, titulo: 'PROGRAMÁTICA YOUTUBE' }
    } as const;

    type TipoFormato = keyof typeof formatosEstrutura;

    // Criar seções apenas para os formatos selecionados
    let linhaAtual = 11;
    
    // Processar todas as configurações de Programática Open
    if (formData.configuracoesProgramaticaOpen && formData.configuracoesProgramaticaOpen.length > 0) {
      for (const configuracao of formData.configuracoesProgramaticaOpen) {
        // Para cada formato na configuração, criar uma seção
        for (const formato of configuracao.formato) {
          const estrutura = formatosEstrutura[formato as TipoFormato];
          
          // Título da seção
          worksheet.mergeCells(`B${linhaAtual}:C${linhaAtual}`);
          const titleCell = worksheet.getCell(`B${linhaAtual}`);
          titleCell.value = estrutura.titulo;
          Object.assign(titleCell, estilos.cabecalhoPrincipal);

          // Obter o valor CPM/CPV baseado na região selecionada
          console.log('=== Parâmetros para getCPM ===');
          console.log('Objetivo:', configuracao.objetivo);
          console.log('Plataforma:', configuracao.plataforma);
          console.log('Formato:', formato);
          console.log('Estratégias:', configuracao.estrategiasPorFormato[formato]?.join(', ') || '');
          
          // --- CORREÇÃO PARA YOUTUBE/BRANDFORMANCE ---
          let formatoParaCPM = formato;
          let tipoCompraParaCPM = configuracao.comprasPorFormato[formato]?.[0] || 'CPM';
          if (
            configuracao.objetivo === 'Brandformance' &&
            (formato === 'Youtube' || formato === 'YouTube')
          ) {
            formatoParaCPM = 'YouTube'; // padronizar para o DEMO_DATA
            const estrategia = configuracao.estrategiasPorFormato[formato]?.[0] || '';
            if (estrategia === 'Non-Skippable, In Stream, Discovery e Bumper') {
              tipoCompraParaCPM = 'CPM';
            } else {
              tipoCompraParaCPM = 'CPV';
            }
          }
          console.log('Tipo de Compra:', tipoCompraParaCPM);
          console.log('Região:', configuracao.regiao);

          const cpmValue = getCPM(
            configuracao.objetivo,
            configuracao.plataforma,
            formatoParaCPM,
            configuracao.estrategiasPorFormato[formato]?.join(', ') || '',
            tipoCompraParaCPM,
            configuracao.regiao as Regiao,
            formData.bv
          );

          console.log('Valor CPM/CPV retornado:', cpmValue);

          // Cabeçalhos das colunas
          const headerRow = linhaAtual + 2;
          const cabecalhos = [
            { col: 'B', valor: 'OBJETIVO' },
            { col: 'C', valor: 'ESTRATÉGIAS' },
            { col: 'D', valor: 'DEVICE' },
            { col: 'E', valor: 'PLATAFORMA' },
            { col: 'F', valor: 'FORMATOS' },
            { col: 'G', valor: 'TARGET' },
            { col: 'H', valor: 'INTERESSES' },
            { col: 'I', valor: 'PRAÇA' },
            { col: 'J', valor: 'IMPRESSÕES' },
            { col: 'K', valor: configuracao.comprasPorFormato[formato]?.[0] === 'CPM' ? `CPM` : `CPV` },
            { col: 'L', valor: formData.comissao ? 'BRUTO' : 'LIQUIDO' },
            { col: 'M', valor: formData.comissao ? 'LIQUIDO' : '' },
            { col: formData.comissao ? 'O' : 'N', valor: (formato === 'Youtube' || formato === 'YouTube' || formato === 'Vídeo Open') ? 'VTR' : (configuracao.comprasPorFormato[formato]?.[0] === 'CPV' ? 'VTR' : 'CTR') },
            { col: formData.comissao ? 'P' : 'O', valor: (formato === 'Youtube' || formato === 'YouTube' || formato === 'Vídeo Open') ? 'VIEWS' : (configuracao.comprasPorFormato[formato]?.[0] === 'CPV' ? 'VIEWS' : 'CLIQUES') },
            { col: formData.comissao ? 'Q' : 'P', valor: 'VIEWABILITY' }
          ];

          cabecalhos.forEach(cab => {
            if (!formData.comissao && cab.col === 'M') return;
            
            const cell = worksheet.getCell(`${cab.col}${headerRow}`);
            cell.value = cab.valor;
            Object.assign(cell, {
              ...estilos.cabecalhoPrincipal,
              border: {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } }
              }
            });
          });

          // Linha de dados
          const dataRow = headerRow + 1;
          
          // Mesclar colunas B e C
          if (!worksheet.getCell(`B${dataRow}`).isMerged) {
            worksheet.mergeCells(`B${dataRow}:B${dataRow + 3}`);
          }
          if (!worksheet.getCell(`C${dataRow}`).isMerged) {
            worksheet.mergeCells(`C${dataRow}:C${dataRow + 2}`);
          }

          // Mesclar colunas de D até L ou M, dependendo se é comissão
          const ultimaColuna = formData.comissao ? 'M' : 'L';
          for (let col = 'D'; col <= ultimaColuna; col = String.fromCharCode(col.charCodeAt(0) + 1)) {
            if (!worksheet.getCell(`${col}${dataRow}`).isMerged) {
              worksheet.mergeCells(`${col}${dataRow}:${col}${dataRow + 2}`);
            }
          }

          // Mesclar colunas de CTR/VTR, CLIQUES/VIEWS e VIEWABILITY
          const colunaInicial = formData.comissao ? 'O' : 'N';
          const colunaFinal = formData.comissao ? 'Q' : 'P';
          for (let col = colunaInicial; col <= colunaFinal; col = String.fromCharCode(col.charCodeAt(0) + 1)) {
            if (!worksheet.getCell(`${col}${dataRow}`).isMerged) {
              worksheet.mergeCells(`${col}${dataRow}:${col}${dataRow + 2}`);
            }
          }

          // Mesclar e preencher linha cinza (brand safety)
          const linhaAbaixo = dataRow + 3;
          const colC = 'C';
          const colM = 'M';
          const colL = 'L';
          const colO = 'O';
          const colQ = 'Q';
          // C até M/L
          const colEndMain = formData.comissao ? colM : colL;
          worksheet.mergeCells(`${colC}${linhaAbaixo}:${colEndMain}${linhaAbaixo}`);
          const cellBrandSafety = worksheet.getCell(`${colC}${linhaAbaixo}`);
          cellBrandSafety.value = 'Brand Safety/Brand Suitability - O algoritmo de brand safety protege em 95% a marca de marketing negativo.';
          Object.assign(cellBrandSafety, {
            ...estilos.brandSafety,
            border: {
              top: { style: 'thin', color: { argb: 'FF000000' } },
              left: { style: 'thin', color: { argb: 'FF000000' } },
              bottom: { style: 'thin', color: { argb: 'FF000000' } },
              right: { style: 'thin', color: { argb: 'FF000000' } }
            }
          });
          // O até Q / N até P
          const colStartSide = formData.comissao ? colO : 'N';
          const colEndSide = formData.comissao ? colQ : 'P';
          worksheet.mergeCells(`${colStartSide}${linhaAbaixo}:${colEndSide}${linhaAbaixo}`);
          const cellBrandSafetySide = worksheet.getCell(`${colStartSide}${linhaAbaixo}`);
          cellBrandSafetySide.value = '';
          Object.assign(cellBrandSafetySide, {
            ...estilos.brandSafety,
            border: {
              top: { style: 'thin', color: { argb: 'FF000000' } },
              left: { style: 'thin', color: { argb: 'FF000000' } },
              bottom: { style: 'thin', color: { argb: 'FF000000' } },
              right: { style: 'thin', color: { argb: 'FF000000' } }
            }
          });
          // Mesclar e preencher linha roxa abaixo
          const linhaRoxa = linhaAbaixo + 1;
          worksheet.mergeCells(`B${linhaRoxa}:${colEndMain}${linhaRoxa}`);
          const cellRoxa = worksheet.getCell(`B${linhaRoxa}`);
          Object.assign(cellRoxa, estilos.cabecalhoPrincipal);
          worksheet.mergeCells(`${colStartSide}${linhaRoxa}:${colEndSide}${linhaRoxa}`);
          const cellRoxaSide = worksheet.getCell(`${colStartSide}${linhaRoxa}`);
          Object.assign(cellRoxaSide, estilos.cabecalhoPrincipal);

          // Preencher dados
          const cells = [
            { col: 'B', valor: configuracao.objetivo },
            { col: 'C', valor: configuracao.estrategiasPorFormato[formato]?.join(', ') || '' },
            { col: 'D', valor: configuracao.device },
            { col: 'E', valor: configuracao.plataforma },
            { col: 'F', valor: formato }
          ];

          cells.forEach(c => {
            const cell = worksheet.getCell(`${c.col}${dataRow}`);
            cell.value = c.valor;
            Object.assign(cell, estilos.dadosNormais);
          });

          // Preencher o valor CPM/CPV
          const cpmCell = worksheet.getCell(`K${dataRow}`);
          cpmCell.value = cpmValue || 0;
          Object.assign(cpmCell, estilos.dadosNormais);
          cpmCell.numFmt = 'R$ #,##0.00';

          // Target com faixa etária
          const idades = (configuracao.idade || []).map((faixa: string) => {
              const [min, max] = faixa.split('-');
              return {
                min: parseInt(min),
                max: parseInt(max?.replace('+', '') || min)
              };
            });
            
          const menorIdade = idades.length > 0 ? Math.min(...idades.map((i: { min: number; max: number }) => i.min)) : '';
          const maiorIdade = idades.length > 0 ? Math.max(...idades.map((i: { min: number; max: number }) => i.max)) : '';
            
          // Target para Programática Open
          let targetValueTecnicas = '';
          if (configuracao.target && idades.length > 0 && menorIdade !== '' && maiorIdade !== '') {
            if (configuracao.target === 'Ambos Sexos') {
              targetValueTecnicas = `AS - ${menorIdade}-${maiorIdade}${maiorIdade === 65 ? '+' : ''}`;
            } else {
              targetValueTecnicas = `${configuracao.target} - ${menorIdade}-${maiorIdade}${maiorIdade === 65 ? '+' : ''}`;
            }
            console.log('=== PROGRAMÁTICA OPEN ===');
            console.log('Target selecionado:', configuracao.target);
            console.log('Faixas etárias:', idades);
            console.log('Target formatado:', targetValueTecnicas);
          }

          // Target para Programática Especial
          let targetValueEspeciais = '';
          if (formData.habilitarEspeciais && formData.formDataEspeciais) {
            const idadesEspeciais = (formData.formDataEspeciais.idade || []).map((faixa: string) => {
              const [min, max] = faixa.split('-');
              return {
                min: parseInt(min),
                max: parseInt(max?.replace('+', '') || min)
              };
            });
            
            const menorIdadeEspecial = idadesEspeciais.length > 0 ? Math.min(...idadesEspeciais.map((i: { min: number; max: number }) => i.min)) : '';
            const maiorIdadeEspecial = idadesEspeciais.length > 0 ? Math.max(...idadesEspeciais.map((i: { min: number; max: number }) => i.max)) : '';
            
            if (idadesEspeciais.length > 0 && menorIdadeEspecial !== '' && maiorIdadeEspecial !== '') {
              if (formData.formDataEspeciais.target === 'Ambos Sexos') {
                targetValueEspeciais = `AS - ${menorIdadeEspecial}-${maiorIdadeEspecial}${maiorIdadeEspecial === 65 ? '+' : ''}`;
              } else {
                targetValueEspeciais = `${formData.formDataEspeciais.target} - ${menorIdadeEspecial}-${maiorIdadeEspecial}${maiorIdadeEspecial === 65 ? '+' : ''}`;
              }
              console.log('=== PROGRAMÁTICA ESPECIAL ===');
              console.log('Target selecionado:', formData.formDataEspeciais.target);
              console.log('Faixas etárias:', idadesEspeciais);
              console.log('Target formatado:', targetValueEspeciais);
            }
          }

          // Usar o target apropriado baseado no contexto
          const targetValue = targetValueTecnicas;

          const cell = worksheet.getCell(`G${dataRow}`);
          cell.value = targetValue;
          Object.assign(cell, estilos.dadosNormais);

          // Outros dados
          const cellsInteressePraca = [
            { col: 'H', valor: configuracao.interesse },
            { col: 'I', valor: configuracao.pracasPorFormato?.[formato] || '' }
          ];

          cellsInteressePraca.forEach(c => {
            const cell = worksheet.getCell(`${c.col}${dataRow}`);
            cell.value = c.valor;
            Object.assign(cell, {
              ...estilos.dadosNormais,
              border: {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } }
              }
            });
          });

          // Calcular valores monetários e impressões
          const valorNumerico = parseFloat(configuracao.valorPorFormato[formato] || '0') / 100;
          let valorBruto, valorLiquido;
            
          if (formData.comissao) {
            valorLiquido = valorNumerico;
            valorBruto = valorNumerico * 1.25;
          } else {
            valorBruto = valorNumerico;
            valorLiquido = valorNumerico;
          }

          console.log('=== CÁLCULO DE IMPRESSÕES ===');
          console.log('Formato:', formato);
          console.log('Valor numérico:', valorNumerico);
          console.log('Valor líquido:', valorLiquido);
          console.log('CPM/CPV Value:', cpmValue);
          console.log('Tipo de compra:', configuracao.comprasPorFormato[formato]?.[0]);

          let impressoes = 0;
          let views = 0;

          if (cpmValue !== null) {
            if (formato === 'Vídeo Open' || formato === 'Youtube') {
              if (configuracao.comprasPorFormato[formato]?.[0] === 'CPV') {
                // Para CPV, primeiro calculamos as views
                views = valorLiquido / cpmValue;
                // Depois calculamos as impressões baseadas nas views
                impressoes = Math.round((views * 100) / 35);
                console.log('CPV - Views calculadas:', views);
                console.log('CPV - Impressões calculadas:', impressoes);
              } else {
                // Para CPM, primeiro calculamos as impressões
                impressoes = Math.round((valorLiquido / cpmValue) * 1000);
                // Depois calculamos as views baseadas nas impressões
                views = Math.round((impressoes * 35) / 100);
                console.log('CPM Vídeo - Impressões calculadas:', impressoes);
                console.log('CPM Vídeo - Views calculadas:', views);
              }
            } else {
              // Para CPM, calculamos as impressões diretamente
              impressoes = Math.round((valorLiquido / cpmValue) * 1000);
              console.log('CPM Display - Impressões calculadas:', impressoes);
            }
          } else {
            console.log('ERRO: cpmValue é null!');
          }

          const cellImpressoes = worksheet.getCell(`J${dataRow}`);
          cellImpressoes.value = impressoes;
          Object.assign(cellImpressoes, estilos.dadosNormais);
          cellImpressoes.numFmt = '#,##0';

          // Configurar células monetárias
          const colunasMonetarias = formData.comissao ? ['K', 'L', 'M'] : ['K', 'L'];
          colunasMonetarias.forEach(col => {
            const cell = worksheet.getCell(`${col}${dataRow}`);
            Object.assign(cell, estilos.dadosNormais);
            cell.numFmt = 'R$ #,##0.00';
          });

          worksheet.getCell(`L${dataRow}`).value = valorBruto;
          if (formData.comissao) {
            worksheet.getCell(`M${dataRow}`).value = valorLiquido;
          }

          // Configurar CTR/VTR e Views/Cliques
          if ((formato === 'Display IAB' || formato === 'Native') && configuracao.ctrPorFormato?.[formato]) {
            const rawValue = configuracao.ctrPorFormato[formato].replace(',', '.');
            const ctr = parseFloat(rawValue) / 100;
            const cellCtr = worksheet.getCell(`${formData.comissao ? 'O' : 'N'}${dataRow}`);
            cellCtr.value = ctr;
            Object.assign(cellCtr, {
              ...estilos.dadosNormais,
              border: {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } }
              }
            });
            cellCtr.numFmt = '0.00%';
            
            // Calcular cliques baseado no CTR
            const cliques = Math.round(impressoes * ctr);
            const cellCliques = worksheet.getCell(`${formData.comissao ? 'P' : 'O'}${dataRow}`);
            cellCliques.value = cliques;
            Object.assign(cellCliques, {
              ...estilos.dadosNormais,
              border: {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } }
              }
            });
            cellCliques.numFmt = '#,##0';
          } else if (formato === 'Vídeo Open' || formato === 'Youtube') {
            // Para vídeo, usar VTR e Views
            const cellVtr = worksheet.getCell(`${formData.comissao ? 'O' : 'N'}${dataRow}`);
            let vtr = 0.35; // valor padrão
            if (configuracao.ctrPorFormato?.[formato]) {
              const rawValue = configuracao.ctrPorFormato[formato].replace(',', '.');
              const parsed = parseFloat(rawValue);
              if (!isNaN(parsed)) {
                vtr = parsed / 100;
              }
            }
            cellVtr.value = vtr;
            Object.assign(cellVtr, {
              ...estilos.dadosNormais,
              border: {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } }
              }
            });
            cellVtr.numFmt = '0.00%';
            
            const cellViews = worksheet.getCell(`${formData.comissao ? 'P' : 'O'}${dataRow}`);
            cellViews.value = views;
            Object.assign(cellViews, {
              ...estilos.dadosNormais,
              border: {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } }
              }
            });
            cellViews.numFmt = '#,##0';
          }
          // Sempre preencher e estilizar a célula de Viewability
          const cellViewability = worksheet.getCell(`${formData.comissao ? 'Q' : 'P'}${dataRow}`);
          cellViewability.value = '70%';
          Object.assign(cellViewability, {
            ...estilos.dadosNormais,
            border: {
              top: { style: 'thin', color: { argb: 'FF000000' } },
              left: { style: 'thin', color: { argb: 'FF000000' } },
              bottom: { style: 'thin', color: { argb: 'FF000000' } },
              right: { style: 'thin', color: { argb: 'FF000000' } }
            }
          });
          cellViewability.numFmt = '0.00%';

          // Atualizar linhaAtual para o próximo formato
          linhaAtual = linhaRoxa + 3;
        }
      }
    } else {
      // Fallback para o sistema antigo (compatibilidade)
    formData.formato.forEach(formato => {
      const estrutura = formatosEstrutura[formato as TipoFormato];
      
      // Título da seção
      worksheet.mergeCells(`B${linhaAtual}:C${linhaAtual}`);
      const titleCell = worksheet.getCell(`B${linhaAtual}`);
      titleCell.value = estrutura.titulo;
      Object.assign(titleCell, estilos.cabecalhoPrincipal);

      // Obter o valor CPM/CPV baseado na região selecionada
      console.log('=== Parâmetros para getCPM ===');
      console.log('Objetivo:', formData.objetivo);
      console.log('Plataforma:', formData.plataforma);
      console.log('Formato:', formato);
      console.log('Estratégias:', formData.estrategiasPorFormato[formato]?.join(', ') || '');
      
      // --- CORREÇÃO PARA YOUTUBE/BRANDFORMANCE ---
      let formatoParaCPM = formato;
      let tipoCompraParaCPM = formData.comprasPorFormato[formato]?.[0] || 'CPM';
      if (
        formData.objetivo === 'Brandformance' &&
        (formato === 'Youtube' || formato === 'YouTube')
      ) {
        formatoParaCPM = 'YouTube'; // padronizar para o DEMO_DATA
        const estrategia = formData.estrategiasPorFormato[formato]?.[0] || '';
        if (estrategia === 'Non-Skippable, In Stream, Discovery e Bumper') {
          tipoCompraParaCPM = 'CPM';
        } else {
          tipoCompraParaCPM = 'CPV';
        }
      }
      console.log('Tipo de Compra:', tipoCompraParaCPM);
      console.log('Região:', formData.regiao);

      const cpmValue = getCPM(
        formData.objetivo,
        formData.plataforma,
        formatoParaCPM,
        formData.estrategiasPorFormato[formato]?.join(', ') || '',
        tipoCompraParaCPM,
        formData.regiao as Regiao,
        formData.bv
      );

      console.log('Valor CPM/CPV retornado:', cpmValue);

      // Cabeçalhos das colunas
      const headerRow = linhaAtual + 2;
      const cabecalhos = [
        { col: 'B', valor: 'OBJETIVO' },
        { col: 'C', valor: 'ESTRATÉGIAS' },
        { col: 'D', valor: 'DEVICE' },
        { col: 'E', valor: 'PLATAFORMA' },
        { col: 'F', valor: 'FORMATOS' },
        { col: 'G', valor: 'TARGET' },
        { col: 'H', valor: 'INTERESSES' },
        { col: 'I', valor: 'PRAÇA' },
        { col: 'J', valor: 'IMPRESSÕES' },
        { col: 'K', valor: formData.comprasPorFormato[formato]?.[0] === 'CPM' ? `CPM` : `CPV` },
        { col: 'L', valor: formData.comissao ? 'BRUTO' : 'LIQUIDO' },
        { col: 'M', valor: formData.comissao ? 'LIQUIDO' : '' },
        { col: formData.comissao ? 'O' : 'N', valor: (formato === 'Youtube' || formato === 'YouTube' || formato === 'Vídeo Open') ? 'VTR' : (formData.comprasPorFormato[formato]?.[0] === 'CPV' ? 'VTR' : 'CTR') },
        { col: formData.comissao ? 'P' : 'O', valor: (formato === 'Youtube' || formato === 'YouTube' || formato === 'Vídeo Open') ? 'VIEWS' : (formData.comprasPorFormato[formato]?.[0] === 'CPV' ? 'VIEWS' : 'CLIQUES') },
        { col: formData.comissao ? 'Q' : 'P', valor: 'VIEWABILITY' }
      ];

      cabecalhos.forEach(cab => {
        if (!formData.comissao && cab.col === 'M') return;
        
        const cell = worksheet.getCell(`${cab.col}${headerRow}`);
        cell.value = cab.valor;
        Object.assign(cell, {
          ...estilos.cabecalhoPrincipal,
          border: {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          }
        });
      });

      // Linha de dados
      const dataRow = headerRow + 1;
      
      // Mesclar colunas B e C
      if (!worksheet.getCell(`B${dataRow}`).isMerged) {
        worksheet.mergeCells(`B${dataRow}:B${dataRow + 3}`);
      }
      if (!worksheet.getCell(`C${dataRow}`).isMerged) {
        worksheet.mergeCells(`C${dataRow}:C${dataRow + 2}`);
      }

      // Mesclar colunas de D até L ou M, dependendo se é comissão
      const ultimaColuna = formData.comissao ? 'M' : 'L';
      for (let col = 'D'; col <= ultimaColuna; col = String.fromCharCode(col.charCodeAt(0) + 1)) {
        if (!worksheet.getCell(`${col}${dataRow}`).isMerged) {
          worksheet.mergeCells(`${col}${dataRow}:${col}${dataRow + 2}`);
        }
      }

      // Mesclar colunas de CTR/VTR, CLIQUES/VIEWS e VIEWABILITY
      const colunaInicial = formData.comissao ? 'O' : 'N';
      const colunaFinal = formData.comissao ? 'Q' : 'P';
      for (let col = colunaInicial; col <= colunaFinal; col = String.fromCharCode(col.charCodeAt(0) + 1)) {
        if (!worksheet.getCell(`${col}${dataRow}`).isMerged) {
          worksheet.mergeCells(`${col}${dataRow}:${col}${dataRow + 2}`);
        }
      }

      // Mesclar e preencher linha cinza (brand safety)
      const linhaAbaixo = dataRow + 3;
      const colC = 'C';
      const colM = 'M';
      const colL = 'L';
      const colO = 'O';
      const colQ = 'Q';
      // C até M/L
      const colEndMain = formData.comissao ? colM : colL;
      worksheet.mergeCells(`${colC}${linhaAbaixo}:${colEndMain}${linhaAbaixo}`);
      const cellBrandSafety = worksheet.getCell(`${colC}${linhaAbaixo}`);
      cellBrandSafety.value = 'Brand Safety/Brand Suitability - O algoritmo de brand safety protege em 95% a marca de marketing negativo.';
      Object.assign(cellBrandSafety, {
        ...estilos.brandSafety,
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      });
      // O até Q / N até P
      const colStartSide = formData.comissao ? colO : 'N';
      const colEndSide = formData.comissao ? colQ : 'P';
      worksheet.mergeCells(`${colStartSide}${linhaAbaixo}:${colEndSide}${linhaAbaixo}`);
      const cellBrandSafetySide = worksheet.getCell(`${colStartSide}${linhaAbaixo}`);
      cellBrandSafetySide.value = '';
      Object.assign(cellBrandSafetySide, {
        ...estilos.brandSafety,
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      });
      // Mesclar e preencher linha roxa abaixo
      const linhaRoxa = linhaAbaixo + 1;
      worksheet.mergeCells(`B${linhaRoxa}:${colEndMain}${linhaRoxa}`);
      const cellRoxa = worksheet.getCell(`B${linhaRoxa}`);
      Object.assign(cellRoxa, estilos.cabecalhoPrincipal);
      worksheet.mergeCells(`${colStartSide}${linhaRoxa}:${colEndSide}${linhaRoxa}`);
      const cellRoxaSide = worksheet.getCell(`${colStartSide}${linhaRoxa}`);
      Object.assign(cellRoxaSide, estilos.cabecalhoPrincipal);

      // Preencher dados
      const cells = [
        { col: 'B', valor: formData.objetivo },
        { col: 'C', valor: formData.estrategiasPorFormato[formato]?.join(', ') || '' },
        { col: 'D', valor: formData.device },
        { col: 'E', valor: formData.plataforma },
        { col: 'F', valor: formato }
      ];

      cells.forEach(c => {
        const cell = worksheet.getCell(`${c.col}${dataRow}`);
        cell.value = c.valor;
        Object.assign(cell, estilos.dadosNormais);
      });

      // Preencher o valor CPM/CPV
      const cpmCell = worksheet.getCell(`K${dataRow}`);
      cpmCell.value = cpmValue || 0;
      Object.assign(cpmCell, estilos.dadosNormais);
      cpmCell.numFmt = 'R$ #,##0.00';

      // Target com faixa etária
      const idades = (formData.idade || []).map((faixa: string) => {
          const [min, max] = faixa.split('-');
          return {
            min: parseInt(min),
            max: parseInt(max?.replace('+', '') || min)
          };
        });
        
      const menorIdade = idades.length > 0 ? Math.min(...idades.map((i: { min: number; max: number }) => i.min)) : '';
      const maiorIdade = idades.length > 0 ? Math.max(...idades.map((i: { min: number; max: number }) => i.max)) : '';
        
      // Target para Programática Open
      let targetValueTecnicas = '';
      if (formData.target && idades.length > 0 && menorIdade !== '' && maiorIdade !== '') {
        if (formData.target === 'Ambos Sexos') {
          targetValueTecnicas = `AS - ${menorIdade}-${maiorIdade}${maiorIdade === 65 ? '+' : ''}`;
        } else {
          targetValueTecnicas = `${formData.target} - ${menorIdade}-${maiorIdade}${maiorIdade === 65 ? '+' : ''}`;
        }
        console.log('=== PROGRAMÁTICA OPEN ===');
        console.log('Target selecionado:', formData.target);
        console.log('Faixas etárias:', idades);
        console.log('Target formatado:', targetValueTecnicas);
      }

      // Target para Programática Especial
      let targetValueEspeciais = '';
      if (formData.habilitarEspeciais && formData.formDataEspeciais) {
        const idadesEspeciais = (formData.formDataEspeciais.idade || []).map((faixa: string) => {
          const [min, max] = faixa.split('-');
          return {
            min: parseInt(min),
            max: parseInt(max?.replace('+', '') || min)
          };
        });
        
        const menorIdadeEspecial = idadesEspeciais.length > 0 ? Math.min(...idadesEspeciais.map((i: { min: number; max: number }) => i.min)) : '';
        const maiorIdadeEspecial = idadesEspeciais.length > 0 ? Math.max(...idadesEspeciais.map((i: { min: number; max: number }) => i.max)) : '';
        
        if (idadesEspeciais.length > 0 && menorIdadeEspecial !== '' && maiorIdadeEspecial !== '') {
          if (formData.formDataEspeciais.target === 'Ambos Sexos') {
            targetValueEspeciais = `AS - ${menorIdadeEspecial}-${maiorIdadeEspecial}${maiorIdadeEspecial === 65 ? '+' : ''}`;
          } else {
            targetValueEspeciais = `${formData.formDataEspeciais.target} - ${menorIdadeEspecial}-${maiorIdadeEspecial}${maiorIdadeEspecial === 65 ? '+' : ''}`;
          }
          console.log('=== PROGRAMÁTICA ESPECIAL ===');
          console.log('Target selecionado:', formData.formDataEspeciais.target);
          console.log('Faixas etárias:', idadesEspeciais);
          console.log('Target formatado:', targetValueEspeciais);
        }
      }

      // Usar o target apropriado baseado no contexto
      const targetValue = targetValueTecnicas;

      const cell = worksheet.getCell(`G${dataRow}`);
      cell.value = targetValue;
      Object.assign(cell, estilos.dadosNormais);

      // Outros dados
      const cellsInteressePraca = [
        { col: 'H', valor: formData.interesse },
          { col: 'I', valor: formData.pracasPorFormato?.[formato] || formData.praca?.[formato] || '' }
      ];

      cellsInteressePraca.forEach(c => {
        const cell = worksheet.getCell(`${c.col}${dataRow}`);
        cell.value = c.valor;
        Object.assign(cell, {
          ...estilos.dadosNormais,
          border: {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          }
        });
      });

      // Calcular valores monetários e impressões
      const valorNumerico = parseFloat(formData.valorPorFormato[formato] || '0') / 100;
      let valorBruto, valorLiquido;
        
      if (formData.comissao) {
        valorLiquido = valorNumerico;
        valorBruto = valorNumerico * 1.25;
      } else {
        valorBruto = valorNumerico;
        valorLiquido = valorNumerico;
      }

      let impressoes = 0;
      let views = 0;

      if (cpmValue !== null) {
        if (formato === 'Vídeo Open' || formato === 'Youtube') {
          if (formData.comprasPorFormato[formato]?.[0] === 'CPV') {
            // Para CPV, primeiro calculamos as views
            views = valorLiquido / cpmValue;
            // Depois calculamos as impressões baseadas nas views
            impressoes = Math.round((views * 100) / 35);
          } else {
            // Para CPM, primeiro calculamos as impressões
            impressoes = Math.round((valorLiquido / cpmValue) * 1000);
            // Depois calculamos as views baseadas nas impressões
            views = Math.round((impressoes * 35) / 100);
          }
        } else {
          // Para CPM, calculamos as impressões diretamente
          impressoes = Math.round((valorLiquido / cpmValue) * 1000);
        }
      }

      const cellImpressoes = worksheet.getCell(`J${dataRow}`);
      cellImpressoes.value = impressoes;
      Object.assign(cellImpressoes, estilos.dadosNormais);
      cellImpressoes.numFmt = '#,##0';

      // Configurar células monetárias
      const colunasMonetarias = formData.comissao ? ['K', 'L', 'M'] : ['K', 'L'];
      colunasMonetarias.forEach(col => {
        const cell = worksheet.getCell(`${col}${dataRow}`);
        Object.assign(cell, estilos.dadosNormais);
        cell.numFmt = 'R$ #,##0.00';
      });

      worksheet.getCell(`L${dataRow}`).value = valorBruto;
      if (formData.comissao) {
        worksheet.getCell(`M${dataRow}`).value = valorLiquido;
      }

      // Configurar CTR/VTR e Views/Cliques
      if ((formato === 'Display IAB' || formato === 'Native') && formData.ctrPorFormato?.[formato]) {
        const rawValue = formData.ctrPorFormato[formato].replace(',', '.');
        const ctr = parseFloat(rawValue) / 100;
        const cellCtr = worksheet.getCell(`${formData.comissao ? 'O' : 'N'}${dataRow}`);
        cellCtr.value = ctr;
        Object.assign(cellCtr, {
          ...estilos.dadosNormais,
          border: {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          }
        });
        cellCtr.numFmt = '0.00%';
        
        // Calcular cliques baseado no CTR
        const cliques = Math.round(impressoes * ctr);
        const cellCliques = worksheet.getCell(`${formData.comissao ? 'P' : 'O'}${dataRow}`);
        cellCliques.value = cliques;
        Object.assign(cellCliques, {
          ...estilos.dadosNormais,
          border: {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          }
        });
        cellCliques.numFmt = '#,##0';
      } else if (formato === 'Vídeo Open' || formato === 'Youtube') {
        // Para vídeo, usar VTR e Views
        const cellVtr = worksheet.getCell(`${formData.comissao ? 'O' : 'N'}${dataRow}`);
        let vtr = 0.35; // valor padrão
        if (formData.ctrPorFormato?.[formato]) {
          const rawValue = formData.ctrPorFormato[formato].replace(',', '.');
          const parsed = parseFloat(rawValue);
          if (!isNaN(parsed)) {
            vtr = parsed / 100;
          }
        }
        cellVtr.value = vtr;
        Object.assign(cellVtr, {
          ...estilos.dadosNormais,
          border: {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          }
        });
        cellVtr.numFmt = '0.00%';
        
        const cellViews = worksheet.getCell(`${formData.comissao ? 'P' : 'O'}${dataRow}`);
        cellViews.value = views;
        Object.assign(cellViews, {
          ...estilos.dadosNormais,
          border: {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          }
        });
        cellViews.numFmt = '#,##0';
      }
      // Sempre preencher e estilizar a célula de Viewability
      const cellViewability = worksheet.getCell(`${formData.comissao ? 'Q' : 'P'}${dataRow}`);
      cellViewability.value = '70%';
      Object.assign(cellViewability, {
        ...estilos.dadosNormais,
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      });
      cellViewability.numFmt = '0.00%';

      // Atualizar linhaAtual para o próximo formato
      linhaAtual = linhaRoxa + 3;
    });
    }

    // Adicionar Programática Especial para cada configuração
    if (formData.habilitarEspeciais && formData.configuracoesProgramaticaEspecial) {
      console.log('=== PROCESSANDO CONFIGURAÇÕES ESPECIAIS ===');
      console.log('Número de configurações especiais:', formData.configuracoesProgramaticaEspecial.length);
      
      // Processar cada configuração especial
      formData.configuracoesProgramaticaEspecial.forEach((configEspecial, index) => {
        const linhaEspecial = linhaAtual + 1 + (index * 3); // Espaçamento entre configurações
      worksheet.mergeCells(`B${linhaEspecial}:C${linhaEspecial}`);
      const titleCellEspecial = worksheet.getCell(`B${linhaEspecial}`);
        titleCellEspecial.value = `PROGRAMÁTICA ESPECIAL - CONFIGURAÇÃO ${index + 1}`;
      Object.assign(titleCellEspecial, estilos.cabecalhoPrincipal);

      // LOGS PARA DEBUG
        console.log(`=== CONFIGURAÇÃO ESPECIAL ${index + 1} ===`);
        console.log('configEspecial.formData:', configEspecial.formData);
      console.log('formData.valorPorFormato:', formData.valorPorFormato);
      console.log('formData.comissao:', formData.comissao);

      // Cabeçalhos das configurações especiais (NOVO PADRÃO)
      const headerRowEspecial = linhaEspecial + 2;
      const tipoCompra = configEspecial.formData?.tipoCompra?.toUpperCase() || '';
      const formatoEspecial = configEspecial.formData?.formato || '';
      const plataformaEspecial = configEspecial.formData?.plataforma || '';
      const colunaEspecialHeader = getColunaEspecialHeader(formatoEspecial, tipoCompra).toUpperCase();

      let cabecalhosEspeciais;
      // Condição especial para Áudio/Spotify/Áudio
      const criativoNorm = normalizeStr(configEspecial.formData?.criativo);
      const isAudioSpotifyAudio = formatoEspecial.toLowerCase() === 'áudio' && plataformaEspecial.toLowerCase() === 'spotify' && criativoNorm === 'audio';
      if (isAudioSpotifyAudio) {
        if (formData.comissao) {
          cabecalhosEspeciais = [
        { col: 'B', valor: 'OBJETIVO' },
        { col: 'C', valor: 'FORMATO' },
        { col: 'D', valor: 'ESTRATÉGIA' },
        { col: 'E', valor: 'TARGET' },
        { col: 'F', valor: 'INTERESSE' },
        { col: 'G', valor: 'PRAÇA' },
            { col: 'H', valor: 'IMPRESSÕES' },
            { col: 'I', valor: 'CPM' },
            { col: 'J', valor: 'CLR' },
            { col: 'K', valor: 'BRUTO' },
            { col: 'L', valor: 'LÍQUIDO' }
          ];
        } else {
          cabecalhosEspeciais = [
            { col: 'B', valor: 'OBJETIVO' },
            { col: 'C', valor: 'FORMATO' },
            { col: 'D', valor: 'ESTRATÉGIA' },
            { col: 'E', valor: 'TARGET' },
            { col: 'F', valor: 'INTERESSE' },
            { col: 'G', valor: 'PRAÇA' },
            { col: 'H', valor: 'IMPRESSÕES' },
            { col: 'I', valor: 'CPM' },
            { col: 'J', valor: 'CLR' },
            { col: 'K', valor: 'LÍQUIDO' }
          ];
        }
      } else {
        cabecalhosEspeciais = [
          { col: 'B', valor: 'OBJETIVO' },
          { col: 'C', valor: 'FORMATO' },
          { col: 'D', valor: 'ESTRATÉGIA' },
          { col: 'E', valor: 'TARGET' },
          { col: 'F', valor: 'INTERESSE' },
          { col: 'G', valor: 'PRAÇA' },
          { col: 'H', valor: colunaEspecialHeader },
        { col: 'I', valor: tipoCompra },
          { col: 'J', valor: formData.comissao ? 'BRUTO' : 'LÍQUIDO' }
      ];
      if (formData.comissao) {
          cabecalhosEspeciais.push({ col: 'K', valor: 'LÍQUIDO' });
        }
      }

      cabecalhosEspeciais.forEach(cab => {
        const cell = worksheet.getCell(`${cab.col}${headerRowEspecial}`);
        cell.value = cab.valor;
        Object.assign(cell, {
          ...estilos.cabecalhoPrincipal,
          border: {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          }
        });
      });
      // Dados das configurações especiais (NOVO PADRÃO)
      const dataRowEspecial = headerRowEspecial + 1;
      
      // Calcular valores bruto/liquido
      let valorTotal = 0;
      if (configEspecial.formData && configEspecial.formData.valorMinimoMensal) {
        valorTotal = parseFloat(
          (configEspecial.formData.valorMinimoMensal || '0')
            .toString()
            .replace(/\./g, '') // remove pontos de milhar
            .replace(',', '.')  // troca vírgula decimal por ponto
            .replace(/[^\d.-]/g, '') // remove qualquer outro caractere
        );
      }

      // Tabela padrão de valores para Programática Especial (49 linhas)
      const especiaisValoresPadrao = [
        { objetivo: 'Awareness', plataforma: 'CTV', parceiro: 'ShowHeroes', estrategia: 'CTV LG + TCL - Brasil', formato: 'Vídeo', tipoCompra: 'CPM', valor: 150.00 },
        { objetivo: 'Awareness', plataforma: 'CTV', parceiro: 'Samsung', estrategia: 'CTV Samsung TV Plus  - Brasil', formato: 'Vídeo', tipoCompra: 'CPM', valor: 150.00 },
        { objetivo: 'Awareness', plataforma: 'CTV', parceiro: 'Samsung', estrategia: 'CTV Samsung TV Plus  - Estados e Grandes Metrópoles', formato: 'Vídeo', tipoCompra: 'CPM', valor: 180.00 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'ShowHeroes', estrategia: 'OTT - Brasil', formato: 'Vídeo', tipoCompra: 'CPM', valor: 120.00 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Pluto TV / UOL', estrategia: 'OTT - Pluto TV', formato: 'Vídeo', tipoCompra: 'CPM', valor: 120.00 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Direto', estrategia: 'Rotativo - Aberto BR', formato: 'Vídeo 30s', tipoCompra: 'CPM', valor: 175.00 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Direto', estrategia: 'Gênero do Conteúdo (mínimo 3)', formato: 'Vídeo 30s', tipoCompra: 'CPM', valor: 201.25 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Direto', estrategia: 'Top 10', formato: 'Vídeo 30s', tipoCompra: 'CPM', valor: 245.00 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Direto', estrategia: 'Idade e Gênero (binário)', formato: 'Vídeo 30s', tipoCompra: 'CPM', valor: 220.00 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Direto', estrategia: 'Geoloc (Apenas Estado)', formato: 'Vídeo 30s', tipoCompra: 'CPM', valor: 192.50 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Direto', estrategia: 'Primeira Impressão', formato: 'Vídeo 30s', tipoCompra: 'CPM', valor: 350.00 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Prog', estrategia: 'Aberto BR', formato: 'Vídeo 10s', tipoCompra: 'CPM', valor: 116.00 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Prog', estrategia: 'Aberto BR', formato: 'Vídeo 15s', tipoCompra: 'CPM', valor: 154.67 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Prog', estrategia: 'Aberto BR + Gênero de Conteúdo', formato: 'Vídeo 10s', tipoCompra: 'CPM', valor: 127.60 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Prog', estrategia: 'Aberto BR + Gênero de Conteúdo', formato: 'Vídeo 15s', tipoCompra: 'CPM', valor: 170.13 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Prog', estrategia: 'Estado ou Grandes Metrópoles', formato: 'Vídeo 10s', tipoCompra: 'CPM', valor: 127.60 },
        { objetivo: 'Awareness', plataforma: 'OTT', parceiro: 'Netflix Prog', estrategia: 'Estado ou Grandes Metrópoles', formato: 'Vídeo 15s', tipoCompra: 'CPM', valor: 170.13 },
        { objetivo: 'Awareness', plataforma: 'WhatsApp', parceiro: 'WhatsNow', estrategia: 'Abaixo de 20 mil disparos', formato: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', valor: 0.77, valorComBv: 0.92 },
        { objetivo: 'Awareness', plataforma: 'WhatsApp', parceiro: 'WhatsNow', estrategia: '20 mil a 30 mil disparos', formato: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', valor: 0.70, valorComBv: 0.84 },
        { objetivo: 'Awareness', plataforma: 'WhatsApp', parceiro: 'WhatsNow', estrategia: '31 mil a 200 mil disparos', formato: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', valor: 0.53, valorComBv: 0.64 },
        { objetivo: 'Awareness', plataforma: 'WhatsApp', parceiro: 'WhatsNow', estrategia: '201 mil a 600 mil disparos', formato: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', valor: 0.50, valorComBv: 0.60 },
        { objetivo: 'Awareness', plataforma: 'WhatsApp', parceiro: 'WhatsNow', estrategia: 'A partir de 600 mil disparos', formato: 'Display ou Vídeo + Texto', tipoCompra: 'CPD', valor: 0.43, valorComBv: 0.52 },
        { objetivo: 'Awareness', plataforma: 'Áudio', parceiro: 'Spotify', estrategia: 'Segmentação demográfica e geográfica', formato: 'Display', tipoCompra: 'CPM', valor: 49.20, valorComBv: 59.04 },
        { objetivo: 'Awareness', plataforma: 'Áudio', parceiro: 'Spotify', estrategia: 'Segmentação demográfica e geográfica', formato: 'Vídeo', tipoCompra: 'CPM', valor: 150.37, valorComBv: 180.44 },
        { objetivo: 'Awareness', plataforma: 'Áudio', parceiro: 'Spotify', estrategia: 'Segmentação demográfica e geográfica', formato: 'Audio', tipoCompra: 'CPM', valor: 138.00, valorComBv: 165.60 },
        { objetivo: 'Awareness', plataforma: 'Display', parceiro: 'Serasa', estrategia: 'Site & App Serasa', formato: 'Display', tipoCompra: 'CPM', valor: 95.00, valorComBv: 110.00 },
        { objetivo: 'Consideração', plataforma: 'Push Notification', parceiro: 'Siprocal', estrategia: 'Push Notification (Contextual ou App Target) Com Imagem ou Apenas Texto', formato: 'Display, Logo + Texto', tipoCompra: 'CPC', valor: 2.67, valorComBv: 3.20 },
        { objetivo: 'Consideração', plataforma: 'Push Notification', parceiro: 'Hands', estrategia: 'Hands - Push Notification - Geotrack & Livetrack (Raios mais precisos em lista de endereços)', formato: 'Display, Logo + Texto', tipoCompra: 'CPE', valor: 0.70, valorComBv: 0.85 },
        { objetivo: 'Consideração', plataforma: 'Push Notification', parceiro: 'Hands', estrategia: 'Hands - Push Notification - Geotrack (Raios mais precisos em lista de endereços) + Relatório de Store Visits', formato: 'Display, Logo + Texto', tipoCompra: 'CPE', valor: 1.00, valorComBv: 1.25 },
        { objetivo: 'Consideração', plataforma: 'Web Push', parceiro: 'Pixel Roads', estrategia: 'País/Estado/Grandes Metrópoles', formato: 'Display + Texto', tipoCompra: 'CPC', valor: 1.50 },
        { objetivo: 'Consideração', plataforma: 'Web Push', parceiro: 'Pixel Roads', estrategia: 'Pequenas Praças - Idealmente trabalhar com mais de uma', formato: 'Display + Texto', tipoCompra: 'CPC', valor: 1.85 },
        { objetivo: 'Consideração', plataforma: 'Native', parceiro: 'Pixel Roads', estrategia: 'Native 3.0', formato: 'Native - Imagem + Texto', tipoCompra: 'CPC', valor: 1.83, valorComBv: 2.20 },
        { objetivo: 'Consideração', plataforma: 'Native', parceiro: 'Tinder', estrategia: 'Native Display Card (Square) - DV360 - PD', formato: 'Display', tipoCompra: 'CPM', valor: 150.00, valorComBv: 204.23 },
        { objetivo: 'Consideração', plataforma: 'Native', parceiro: 'Tinder', estrategia: 'Native Display Card - DV360 - PG', formato: 'Display', tipoCompra: 'CPM', valor: 150.00, valorComBv: 204.23 },
        { objetivo: 'Consideração', plataforma: 'Native', parceiro: 'Tinder', estrategia: 'Native Video Card - DV360 - PG', formato: 'Vídeo', tipoCompra: 'CPM', valor: 150.00, valorComBv: 204.23 },
        { objetivo: 'Consideração', plataforma: 'Alto Impacto', parceiro: 'Seedtag', estrategia: 'Contextual Impact Display (CID)', formato: 'Display', tipoCompra: 'CPM', valor: 75.60, valorComBv: 90.72 },
        { objetivo: 'Consideração', plataforma: 'Alto Impacto', parceiro: 'Seedtag', estrategia: 'Contextual Engagement Display (CED)', formato: 'Display', tipoCompra: 'CPM', valor: 73.07, valorComBv: 87.68 },
        { objetivo: 'Consideração', plataforma: 'Alto Impacto', parceiro: 'Seedtag', estrategia: 'Contextual Frame Display (CFD)', formato: 'Display', tipoCompra: 'CPM', valor: 94.50, valorComBv: 113.40 },
        { objetivo: 'Consideração', plataforma: 'Alto Impacto', parceiro: 'Seedtag', estrategia: 'Contextual Branded Video (CBV)', formato: 'Vídeo', tipoCompra: 'CPV', valor: 0.60, valorComBv: 0.72 },
        { objetivo: 'Consideração', plataforma: 'Alto Impacto', parceiro: 'Seedtag', estrategia: 'Contextual Engagement Video (CEV)', formato: 'Vídeo', tipoCompra: 'CPV', valor: 0.77, valorComBv: 0.92 },
        { objetivo: 'Consideração', plataforma: 'Alto Impacto', parceiro: 'Seedtag', estrategia: 'Contextual Outstream Video (COV)', formato: 'Vídeo', tipoCompra: 'CPV', valor: 0.60, valorComBv: 0.72 },
        { objetivo: 'Consideração', plataforma: 'Alto Impacto', parceiro: 'Seedtag', estrategia: 'Contextual Native', formato: 'Display', tipoCompra: 'CPC', valor: 9.60, valorComBv: 11.52 },
        { objetivo: 'Consideração', plataforma: 'Alto Impacto', parceiro: 'Vidoomy', estrategia: 'Vídeo Enriquecido', formato: 'Vídeo', tipoCompra: 'CPM', valor: 90.00, valorComBv: 130.00 },
        { objetivo: 'Consideração', plataforma: 'In Game', parceiro: 'Etermax', estrategia: 'Playable Ads', formato: 'Vídeo', tipoCompra: 'CPM', valor: 208.01 },
        { objetivo: 'Consideração', plataforma: 'In Game', parceiro: 'Etermax', estrategia: 'Interstitial', formato: 'Display ou Vídeo', tipoCompra: 'CPM', valor: 79.42 },
        { objetivo: 'Consideração', plataforma: 'In Game', parceiro: 'Etermax', estrategia: 'Rewarded Vídeo', formato: 'Vídeo', tipoCompra: 'CPM', valor: 147.50 },
        { objetivo: 'Consideração', plataforma: 'In Game', parceiro: 'Etermax', estrategia: 'Banner', formato: 'Display', tipoCompra: 'CPM', valor: 45.38 },
        { objetivo: 'Consideração', plataforma: 'In Game', parceiro: 'Adsmovil', estrategia: 'Interstitial', formato: 'Display ou Vídeo', tipoCompra: 'CPM', valor: 60.00, valorComBv: 80.00 },
        { objetivo: 'Consideração', plataforma: 'In Game', parceiro: 'Adsmovil', estrategia: 'Rewarded Vídeo', formato: 'Vídeo', tipoCompra: 'CPM', valor: 120.00, valorComBv: 160.00 },
      ];

      const especiais = configEspecial.formData || {};
      // Valor do tipo de compra preenchido no painel
      console.log('DEBUG - configEspecial.formData COMPLETO:', JSON.stringify(configEspecial.formData, null, 2));
      let valorTipoCompra = especiais.tabelaPreco || '';

      // Formatar o target com a faixa etária
      const idades = (formData.idade || []).map((faixa: string) => {
        const [min, max] = faixa.split('-');
        return {
          min: parseInt(min),
          max: parseInt(max?.replace('+', '') || min)
        };
      });
      
      const menorIdade = idades.length > 0 ? Math.min(...idades.map((i: { min: number; max: number }) => i.min)) : '';
      const maiorIdade = idades.length > 0 ? Math.max(...idades.map((i: { min: number; max: number }) => i.max)) : '';
      
      // Target para Programática Open
      let targetValueTecnicas = '';
      if (formData.target && idades.length > 0 && menorIdade !== '' && maiorIdade !== '') {
        if (formData.target === 'Ambos Sexos') {
          targetValueTecnicas = `AS - ${menorIdade}-${maiorIdade}${maiorIdade === 65 ? '+' : ''}`;
        } else {
          targetValueTecnicas = `${formData.target} - ${menorIdade}-${maiorIdade}${maiorIdade === 65 ? '+' : ''}`;
        }
        console.log('=== PROGRAMÁTICA OPEN ===');
        console.log('Target selecionado:', formData.target);
        console.log('Faixas etárias:', idades);
        console.log('Target formatado:', targetValueTecnicas);
      }

      // Target para Programática Especial
      let targetValueEspeciais = '';
      if (configEspecial.formData && configEspecial.formData.target) {
        const idadesEspeciais = (configEspecial.formData.idade || []).map((faixa: string) => {
          const [min, max] = faixa.split('-');
          return {
            min: parseInt(min),
            max: parseInt(max?.replace('+', '') || min)
          };
        });
        
        const menorIdadeEspecial = idadesEspeciais.length > 0 ? Math.min(...idadesEspeciais.map((i: { min: number; max: number }) => i.min)) : '';
        const maiorIdadeEspecial = idadesEspeciais.length > 0 ? Math.max(...idadesEspeciais.map((i: { min: number; max: number }) => i.max)) : '';
        
        if (idadesEspeciais.length > 0 && menorIdadeEspecial !== '' && maiorIdadeEspecial !== '') {
          if (configEspecial.formData.target === 'Ambos Sexos') {
            targetValueEspeciais = `AS - ${menorIdadeEspecial}-${maiorIdadeEspecial}${maiorIdadeEspecial === 65 ? '+' : ''}`;
          } else {
            targetValueEspeciais = `${configEspecial.formData.target} - ${menorIdadeEspecial}-${maiorIdadeEspecial}${maiorIdadeEspecial === 65 ? '+' : ''}`;
          }
          console.log('=== PROGRAMÁTICA ESPECIAL ===');
          console.log('Target selecionado:', configEspecial.formData.target);
          console.log('Faixas etárias:', idadesEspeciais);
          console.log('Target formatado:', targetValueEspeciais);
        }
      }

      // Usar o target apropriado baseado no contexto
      const targetValue = targetValueTecnicas;

      // Se não encontrar, buscar na tabela padrão
      if (!valorTipoCompra) {
        const match = especiaisValoresPadrao.find(item =>
          item.objetivo === especiais.objetivo &&
          item.plataforma === especiais.plataforma &&
          item.parceiro === especiais.parceiro &&
          item.estrategia === especiais.estrategia &&
          item.formato === especiais.formato &&
          item.tipoCompra === tipoCompra
        );
        if (match) {
          valorTipoCompra = (formData.bv && match.valorComBv ? match.valorComBv : match.valor).toString().replace('.', ',');
        }
      }
      console.log('tipoCompra:', tipoCompra, 'valorTipoCompra:', valorTipoCompra);
      // Aplica BV se marcado
      let valorNumerico = parseFloat(
        (valorTipoCompra || '0')
          .toString()
          .replace(/\./g, '')
          .replace(',', '.')
          .replace(/[^\d.-]/g, '')
      );
      if (formData.bv && valorNumerico > 0) {
        valorNumerico = valorNumerico * 1.2; // 20% de BV
      }
      // Formata para moeda
      const valorFormatado = (valorTipoCompra && valorNumerico > 0)
        ? valorNumerico.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : 'R$ 0,00';

      // Calcular valores bruto e líquido
      let bruto = valorTotal;
      let liquido = valorTotal;
      if (formData.comissao) {
        bruto = valorTotal * 1.25;
      }

      // Calcular impressões e views baseado no tipo de compra
      let impressoes = 0;
      let views = 0;
      if (valorNumerico > 0) {
        if (tipoCompra === 'CPV') {
          views = Math.round(liquido / valorNumerico);
          impressoes = Math.round((views * 100) / 35);
        } else if (tipoCompra === 'CPM') {
          impressoes = Math.round((liquido / valorNumerico) * 1000);
          views = Math.round((impressoes * 35) / 100);
        }
      }

      // LOGS PARA DEBUG DOS VALORES CALCULADOS
      console.log('valorTotal (especial):', valorTotal);
      console.log('bruto (especial):', bruto);
      console.log('liquido (especial):', liquido);
      console.log('valorTipoCompra:', valorTipoCompra);
      console.log('impressoes:', impressoes);
      console.log('views:', views);

      // Adicionar na célula K o valor numérico puro, não string com 'R$'
      const valorTipoCompraNumerico = parseFloat((valorTipoCompra || '0').toString().replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.')) || 0;

      let cellsEspeciais;
      if (isAudioSpotifyAudio) {
        if (formData.comissao) {
          cellsEspeciais = [
        { col: 'B', valor: especiais.objetivo || '' },
        { col: 'C', valor: especiais.formato || '' },
        { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
        { col: 'F', valor: especiais.interesse || '' },
        { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorTipoCompraNumerico },
            { col: 'J', valor: formatNumberPtBr(Math.round(impressoes * 0.9)) },
            { col: 'K', valor: bruto },
            { col: 'L', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorTipoCompraNumerico },
            { col: 'J', valor: formatNumberPtBr(Math.round(impressoes * 0.9)) },
            { col: 'K', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'áudio' && plataformaEspecial.toLowerCase() === 'spotify' && criativoNorm === 'video') {
        // Debug logs
        console.log('=== DEBUG VTR ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('CTR value:', especiais.ctr);
        
        // Áudio/Spotify/Vídeo: garantir CPM na coluna I
        const vtrValue = especiais.ctr ? parseFloat(especiais.ctr.replace(',', '.')) / 100 : 0.35;
        console.log('VTR value calculated:', vtrValue);
        const views = Math.round(impressoes * vtrValue);
        console.log('Views calculated:', views);

        const bordaCompleta = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };

        // Definir linhaAbaixoEspecial antes do uso
        const linhaAbaixoEspecial = dataRowEspecial + 3;

      if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorTipoCompraNumerico },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido },
            { col: 'O', valor: vtrValue },
            { col: 'P', valor: views }
          ];

          // Adicionar cabeçalhos VTR e VIEWS
          const headerRowVtrViews = headerRowEspecial;
          worksheet.getCell(`O${headerRowVtrViews}`).value = 'VTR';
          worksheet.getCell(`P${headerRowVtrViews}`).value = 'VIEWS';
          Object.assign(worksheet.getCell(`O${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
          Object.assign(worksheet.getCell(`P${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

          // Linha em branco entre cabeçalho e cinza
          worksheet.getCell(`O${headerRowVtrViews + 1}`).value = '';
          worksheet.getCell(`P${headerRowVtrViews + 1}`).value = '';
          worksheet.getCell(`O${headerRowVtrViews + 1}`).value = '';
          worksheet.getCell(`P${headerRowVtrViews + 1}`).value = '';
          Object.assign(worksheet.getCell(`O${headerRowVtrViews + 1}`), { border: bordaCompleta });
          Object.assign(worksheet.getCell(`P${headerRowVtrViews + 1}`), { border: bordaCompleta });

          // Aplicar valores nas células
          const cellVtr = worksheet.getCell(`O${dataRowEspecial}`);
          cellVtr.value = vtrValue;
          cellVtr.numFmt = '0.00%';
          Object.assign(cellVtr, estilos.dadosNormais);

          const cellViews = worksheet.getCell(`P${dataRowEspecial}`);
          cellViews.value = views;
          cellViews.numFmt = '#,##0';
          Object.assign(cellViews, estilos.dadosNormais);

          // Mesclar horizontalmente O e P na linha cinza e na linha roxa
          const linhaCinzaVtrViews = linhaAbaixoEspecial;
          const linhaRoxaVtrViews = linhaAbaixoEspecial + 1;
          worksheet.mergeCells(`O${linhaCinzaVtrViews}:P${linhaCinzaVtrViews}`);
          worksheet.mergeCells(`O${linhaRoxaVtrViews}:P${linhaRoxaVtrViews}`);
          Object.assign(worksheet.getCell(`O${linhaCinzaVtrViews}`), estilos.brandSafety, { border: bordaCompleta });
          Object.assign(worksheet.getCell(`O${linhaRoxaVtrViews}`), estilos.cabecalhoPrincipal);
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorTipoCompraNumerico },
            { col: 'J', valor: liquido },
            { col: 'N', valor: vtrValue },
            { col: 'O', valor: views }
          ];

          // Adicionar cabeçalhos VTR e VIEWS
          const headerRowVtrViews = headerRowEspecial;
          worksheet.getCell(`N${headerRowVtrViews}`).value = 'VTR';
          worksheet.getCell(`O${headerRowVtrViews}`).value = 'VIEWS';
          Object.assign(worksheet.getCell(`N${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
          Object.assign(worksheet.getCell(`O${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

          // Linha em branco entre cabeçalho e cinza
          worksheet.getCell(`N${headerRowVtrViews + 1}`).value = '';
          worksheet.getCell(`O${headerRowVtrViews + 1}`).value = '';
          worksheet.getCell(`N${headerRowVtrViews + 1}`).value = '';
          worksheet.getCell(`O${headerRowVtrViews + 1}`).value = '';
          Object.assign(worksheet.getCell(`N${headerRowVtrViews + 1}`), { border: bordaCompleta });
          Object.assign(worksheet.getCell(`O${headerRowVtrViews + 1}`), { border: bordaCompleta });

          // Aplicar valores nas células
          const cellVtr = worksheet.getCell(`N${dataRowEspecial}`);
          cellVtr.value = vtrValue;
          cellVtr.numFmt = '0.00%';
          Object.assign(cellVtr, estilos.dadosNormais);

          const cellViews = worksheet.getCell(`O${dataRowEspecial}`);
          cellViews.value = views;
          cellViews.numFmt = '#,##0';
          Object.assign(cellViews, estilos.dadosNormais);

          // Mesclar horizontalmente N e O na linha cinza e na linha roxa
          const linhaCinzaVtrViews = linhaAbaixoEspecial;
          const linhaRoxaVtrViews = linhaAbaixoEspecial + 1;
          worksheet.mergeCells(`N${linhaCinzaVtrViews}:O${linhaCinzaVtrViews}`);
          worksheet.mergeCells(`N${linhaRoxaVtrViews}:O${linhaRoxaVtrViews}`);
          Object.assign(worksheet.getCell(`N${linhaCinzaVtrViews}`), estilos.brandSafety, { border: bordaCompleta });
          Object.assign(worksheet.getCell(`N${linhaRoxaVtrViews}`), estilos.cabecalhoPrincipal);
        }
      } else if (formatoEspecial.toLowerCase() === 'áudio' && plataformaEspecial.toLowerCase() === 'spotify' && criativoNorm === 'display') {
        // Debug logs
        console.log('=== DEBUG CPM DISPLAY ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Áudio/Spotify/Display: garantir CPM na coluna I
        const cpmValue = especiais.tabelaPreco ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.')) : 49.20;
        console.log('CPM value calculated:', cpmValue);

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: cpmValue },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: cpmValue },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'whatsapp' || formatoEspecial.toLowerCase() === 'ctv' || formatoEspecial.toLowerCase() === 'ott') {
        // Debug logs
        console.log('=== DEBUG CPM/CPD WHATSAPP/CTV/OTT ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === plataformaEspecial.toLowerCase() &&
          item.estrategia === especiais.estrategia &&
          item.formato === especiais.criativo
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 0;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular disparos para WhatsApp
        let disparos = 0;
        if (formatoEspecial.toLowerCase() === 'whatsapp' && valorCompra > 0) {
          disparos = Math.round(liquido / valorCompra);
        }

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatoEspecial.toLowerCase() === 'whatsapp' ? formatNumberPtBr(disparos) : formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatoEspecial.toLowerCase() === 'whatsapp' ? formatNumberPtBr(disparos) : formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'display' && plataformaEspecial.toLowerCase() === 'serasa') {
        // Debug logs
        console.log('=== DEBUG DISPLAY SERASA ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'display' &&
          item.parceiro.toLowerCase() === 'serasa' &&
          item.formato.toLowerCase() === 'display'
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 95.00;

        console.log('Valor de compra calculado:', valorCompra);

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'push notification' && plataformaEspecial.toLowerCase() === 'siprocal') {
        // Debug logs
        console.log('=== DEBUG PUSH NOTIFICATION SIPROCAL ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'push notification' &&
          item.parceiro.toLowerCase() === 'siprocal' &&
          item.formato.toLowerCase() === 'display, logo + texto'
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 2.67;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular impressões e cliques baseado no CPC
        const cliques = Math.round(liquido / valorCompra);
        const impressoes = Math.round((liquido / valorCompra) * 1000);

        // Adicionar cabeçalhos CTR e CLIQUES
        const headerRowVtrViews = headerRowEspecial;
        const colStartSide = formData.comissao ? 'O' : 'N';
        const colEndSide = formData.comissao ? 'P' : 'O';
        
        const bordaCompleta = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        
        worksheet.getCell(`${colStartSide}${headerRowVtrViews}`).value = 'CTR';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews}`).value = 'CLIQUES';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

        // Linha em branco entre cabeçalho e cinza
        worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`).value = '';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`).value = '';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });

        // Calcular CTR usando o valor do campo CTR do site
        const ctr = especiais.ctr ? parseFloat(especiais.ctr.replace(',', '.')) / 100 : 0.01; // 1% padrão

        // Aplicar valores nas células
        const cellCtr = worksheet.getCell(`${colStartSide}${dataRowEspecial}`);
        cellCtr.value = ctr;
        cellCtr.numFmt = '0.00%';
        Object.assign(cellCtr, estilos.dadosNormais);

        const cellCliques = worksheet.getCell(`${colEndSide}${dataRowEspecial}`);
        cellCliques.value = cliques;
        cellCliques.numFmt = '#,##0';
        Object.assign(cellCliques, estilos.dadosNormais);

        // Atualizar o cabeçalho para IMPRESSÕES
        const headerCell = worksheet.getCell(`H${headerRowEspecial}`);
        headerCell.value = 'IMPRESSÕES';
        Object.assign(headerCell, estilos.cabecalhoPrincipal, { border: bordaCompleta });

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'push notification' && plataformaEspecial.toLowerCase() === 'hands') {
        // Debug logs
        console.log('=== DEBUG PUSH NOTIFICATION HANDS ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'push notification' &&
          item.parceiro.toLowerCase() === 'hands' &&
          item.formato.toLowerCase() === 'display, logo + texto'
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 0.70;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular Envios baseado no CPE
        const envios = Math.round(liquido / valorCompra);

        // Calcular CTR usando o valor do campo CTR do site (dividido por 100 para usar como decimal)
        const ctr = especiais.ctr ? parseFloat(especiais.ctr.replace(',', '.')) / 100 : 0.1; // 10% padrão

        // Calcular cliques baseado no CTR
        const cliques = Math.round(envios * ctr);

        // Adicionar cabeçalhos CTR e CLIQUES
        const headerRowVtrViews = headerRowEspecial;
        const colStartSide = formData.comissao ? 'O' : 'N';
        const colEndSide = formData.comissao ? 'P' : 'O';
        
        const bordaCompleta = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        
        worksheet.getCell(`${colStartSide}${headerRowVtrViews}`).value = 'CTR';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews}`).value = 'CLIQUES';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

        // Linha em branco entre cabeçalho e cinza
        worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`).value = '';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`).value = '';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });

        // Aplicar valores nas células
        const cellCtr = worksheet.getCell(`${colStartSide}${dataRowEspecial}`);
        cellCtr.value = ctr;
        cellCtr.numFmt = '0.00%';
        Object.assign(cellCtr, estilos.dadosNormais);

        const cellCliques = worksheet.getCell(`${colEndSide}${dataRowEspecial}`);
        cellCliques.value = cliques;
        cellCliques.numFmt = '#,##0';
        Object.assign(cellCliques, estilos.dadosNormais);

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(envios) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(envios) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'web push' && plataformaEspecial.toLowerCase() === 'pixel roads') {
        // Debug logs
        console.log('=== DEBUG WEB PUSH PIXEL ROADS ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'web push' &&
          item.parceiro.toLowerCase() === 'pixel roads' &&
          item.formato.toLowerCase() === 'display + texto'
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 1.50;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular impressões e cliques baseado no CPC
        const cliques = Math.round(liquido / valorCompra);
        const impressoes = Math.round((liquido / valorCompra) * 1000);

        // Adicionar cabeçalhos CTR e CLIQUES
        const headerRowVtrViews = headerRowEspecial;
        const colStartSide = formData.comissao ? 'O' : 'N';
        const colEndSide = formData.comissao ? 'P' : 'O';
        
        const bordaCompleta = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        
        worksheet.getCell(`${colStartSide}${headerRowVtrViews}`).value = 'CTR';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews}`).value = 'CLIQUES';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

        // Linha em branco entre cabeçalho e cinza
        worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`).value = '';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`).value = '';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });

        // Calcular CTR usando o valor do campo CTR do site
        const ctr = especiais.ctr ? parseFloat(especiais.ctr.replace(',', '.')) / 100 : 0.01; // 1% padrão

        // Aplicar valores nas células
        const cellCtr = worksheet.getCell(`${colStartSide}${dataRowEspecial}`);
        cellCtr.value = ctr;
        cellCtr.numFmt = '0.00%';
        Object.assign(cellCtr, estilos.dadosNormais);

        const cellCliques = worksheet.getCell(`${colEndSide}${dataRowEspecial}`);
        cellCliques.value = cliques;
        cellCliques.numFmt = '#,##0';
        Object.assign(cellCliques, estilos.dadosNormais);

        // Atualizar o cabeçalho para IMPRESSÕES
        const headerCell = worksheet.getCell(`H${headerRowEspecial}`);
        headerCell.value = 'IMPRESSÕES';
        Object.assign(headerCell, estilos.cabecalhoPrincipal, { border: bordaCompleta });

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'native' && plataformaEspecial.toLowerCase() === 'tinder') {
        // Debug logs
        console.log('=== DEBUG NATIVE TINDER ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'native' &&
          item.parceiro.toLowerCase() === 'tinder' &&
          item.formato.toLowerCase() === 'display'
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 150.00;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular impressões e cliques baseado no CPC
        const cliques = Math.round(liquido / valorCompra);
        const impressoes = Math.round((liquido / valorCompra) * 1000);

        // Adicionar cabeçalhos CTR e CLIQUES
        const headerRowVtrViews = headerRowEspecial;
        const colStartSide = formData.comissao ? 'O' : 'N';
        const colEndSide = formData.comissao ? 'P' : 'O';
        
        const bordaCompleta = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        
        worksheet.getCell(`${colStartSide}${headerRowVtrViews}`).value = 'CTR';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews}`).value = 'CLIQUES';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

        // Linha em branco entre cabeçalho e cinza
        worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`).value = '';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`).value = '';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });

        // Calcular CTR usando o valor do campo CTR do site
        const ctr = especiais.ctr ? parseFloat(especiais.ctr.replace(',', '.')) / 100 : 0.01; // 1% padrão

        // Aplicar valores nas células
        const cellCtr = worksheet.getCell(`${colStartSide}${dataRowEspecial}`);
        cellCtr.value = ctr;
        cellCtr.numFmt = '0.00%';
        Object.assign(cellCtr, estilos.dadosNormais);

        const cellCliques = worksheet.getCell(`${colEndSide}${dataRowEspecial}`);
        cellCliques.value = cliques;
        cellCliques.numFmt = '#,##0';
        Object.assign(cellCliques, estilos.dadosNormais);

        // Atualizar o cabeçalho para IMPRESSÕES
        const headerCell = worksheet.getCell(`H${headerRowEspecial}`);
        headerCell.value = 'IMPRESSÕES';
        Object.assign(headerCell, estilos.cabecalhoPrincipal, { border: bordaCompleta });

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'in game' && (plataformaEspecial.toLowerCase() === 'etermax' || plataformaEspecial.toLowerCase() === 'adsmovil')) {
        // Debug logs
        console.log('=== DEBUG IN GAME ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'in game' &&
          item.parceiro.toLowerCase() === plataformaEspecial.toLowerCase() &&
          item.formato.toLowerCase() === especiais.criativo?.toLowerCase()
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 0;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular impressões e cliques baseado no CPM
        const impressoes = Math.round((liquido / valorCompra) * 1000);
        const cliques = Math.round(impressoes * 0.01); // 1% padrão de CTR

        // Adicionar cabeçalhos CTR e CLIQUES
        const headerRowVtrViews = headerRowEspecial;
        const colStartSide = formData.comissao ? 'O' : 'N';
        const colEndSide = formData.comissao ? 'P' : 'O';
        
        const bordaCompleta = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        
        worksheet.getCell(`${colStartSide}${headerRowVtrViews}`).value = 'CTR';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews}`).value = 'CLIQUES';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

        // Linha em branco entre cabeçalho e cinza
        worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`).value = '';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`).value = '';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });

        // Calcular CTR usando o valor do campo CTR do site
        const ctr = especiais.ctr ? parseFloat(especiais.ctr.replace(',', '.')) / 100 : 0.01; // 1% padrão

        // Aplicar valores nas células
        const cellCtr = worksheet.getCell(`${colStartSide}${dataRowEspecial}`);
        cellCtr.value = ctr;
        cellCtr.numFmt = '0.00%';
        Object.assign(cellCtr, estilos.dadosNormais);

        const cellCliques = worksheet.getCell(`${colEndSide}${dataRowEspecial}`);
        cellCliques.value = cliques;
        cellCliques.numFmt = '#,##0';
        Object.assign(cellCliques, estilos.dadosNormais);

        // Atualizar o cabeçalho para IMPRESSÕES
        const headerCell = worksheet.getCell(`H${headerRowEspecial}`);
        headerCell.value = 'IMPRESSÕES';
        Object.assign(headerCell, estilos.cabecalhoPrincipal, { border: bordaCompleta });

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'native' && plataformaEspecial.toLowerCase() === 'pixel roads') {
        // Debug logs
        console.log('=== DEBUG NATIVE PIXEL ROADS ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'native' &&
          item.parceiro.toLowerCase() === 'pixel roads' &&
          item.formato.toLowerCase() === 'native - imagem + texto'
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 1.83;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular impressões e cliques baseado no CPC
        const cliques = Math.round(liquido / valorCompra);
        const impressoes = Math.round((liquido / valorCompra) * 1000);

        // Adicionar cabeçalhos CTR e CLIQUES
        const headerRowVtrViews = headerRowEspecial;
        const colStartSide = formData.comissao ? 'O' : 'N';
        const colEndSide = formData.comissao ? 'P' : 'O';
        
        const bordaCompleta = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        
        worksheet.getCell(`${colStartSide}${headerRowVtrViews}`).value = 'CTR';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews}`).value = 'CLIQUES';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

        // Linha em branco entre cabeçalho e cinza
        worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`).value = '';
        worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`).value = '';
        Object.assign(worksheet.getCell(`${colStartSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });
        Object.assign(worksheet.getCell(`${colEndSide}${headerRowVtrViews + 1}`), { border: bordaCompleta });

        // Calcular CTR usando o valor do campo CTR do site
        const ctr = especiais.ctr ? parseFloat(especiais.ctr.replace(',', '.')) / 100 : 0.01; // 1% padrão

        // Aplicar valores nas células
        const cellCtr = worksheet.getCell(`${colStartSide}${dataRowEspecial}`);
        cellCtr.value = ctr;
        cellCtr.numFmt = '0.00%';
        Object.assign(cellCtr, estilos.dadosNormais);

        const cellCliques = worksheet.getCell(`${colEndSide}${dataRowEspecial}`);
        cellCliques.value = cliques;
        cellCliques.numFmt = '#,##0';
        Object.assign(cellCliques, estilos.dadosNormais);

        // Atualizar o cabeçalho para IMPRESSÕES
        const headerCell = worksheet.getCell(`H${headerRowEspecial}`);
        headerCell.value = 'IMPRESSÕES';
        Object.assign(headerCell, estilos.cabecalhoPrincipal, { border: bordaCompleta });

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'alto impacto' && plataformaEspecial.toLowerCase() === 'vidoomy') {
        // Debug logs
        console.log('=== DEBUG ALTO IMPACTO VIDOOMY ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'alto impacto' &&
          item.parceiro.toLowerCase() === 'vidoomy' &&
          item.formato.toLowerCase() === 'vídeo'
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 90.00;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular impressões baseado no CPM
        const impressoes = Math.round((liquido / valorCompra) * 1000);

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: formatNumberPtBr(impressoes) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else if (formatoEspecial.toLowerCase() === 'alto impacto' && plataformaEspecial.toLowerCase() === 'seedtag') {
        // Debug logs
        console.log('=== DEBUG ALTO IMPACTO SEEDTAG ===');
        console.log('formData.formDataEspeciais:', formData.formDataEspeciais);
        console.log('especiais:', especiais);
        console.log('tabelaPreco:', especiais.tabelaPreco);
        
        const bordaCompleta = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        
        // Encontrar o valor padrão na tabela
        const valorPadrao = especiaisValoresPadrao.find(item => 
          item.plataforma.toLowerCase() === 'alto impacto' &&
          item.parceiro.toLowerCase() === 'seedtag' &&
          item.estrategia.toLowerCase() === especiais.estrategia.toLowerCase()
        );

        // Usar o valor da tabela de preço ou o valor padrão
        const valorCompra = especiais.tabelaPreco 
          ? parseFloat(especiais.tabelaPreco.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.'))
          : valorPadrao?.valor || 0.92;

        console.log('Valor de compra calculado:', valorCompra);

        // Calcular views, impressões ou cliques baseado no tipo de compra
        let views, impressoes, cliques;
        if (especiais.tipoCompra?.toLowerCase() === 'cpv') {
          views = Math.round(liquido / valorCompra);
          impressoes = Math.round((views * 100) / 35); // Assumindo 35% de VTR
        } else if (especiais.tipoCompra?.toLowerCase() === 'cpc' || especiais.estrategia?.toLowerCase() === 'contextual native') {
          cliques = Math.round(liquido / valorCompra);
          impressoes = Math.round((cliques * 100) / 1); // Assumindo 1% de CTR
        } else {
          impressoes = Math.round((liquido / valorCompra) * 1000);
          views = Math.round(impressoes * 0.35); // Assumindo 35% de VTR
        }

        // Atualizar cabeçalhos
        const headerRowVtrViews = headerRowEspecial;
        if (especiais.estrategia?.toLowerCase() === 'contextual native') {
          worksheet.getCell(`H${headerRowVtrViews}`).value = 'CLIQUES';
          worksheet.getCell(`I${headerRowVtrViews}`).value = 'CPC';
        } else if (especiais.tipoCompra?.toLowerCase() === 'cpv') {
          worksheet.getCell(`H${headerRowVtrViews}`).value = 'VIEWS';
          worksheet.getCell(`I${headerRowVtrViews}`).value = 'CPV';
        } else {
          worksheet.getCell(`H${headerRowVtrViews}`).value = 'IMPRESSÕES';
          worksheet.getCell(`I${headerRowVtrViews}`).value = 'CPM';
        }
        Object.assign(worksheet.getCell(`H${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });
        Object.assign(worksheet.getCell(`I${headerRowVtrViews}`), estilos.cabecalhoPrincipal, { border: bordaCompleta });

        if (formData.comissao) {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: especiais.estrategia?.toLowerCase() === 'contextual native' ? formatNumberPtBr(cliques || 0) : formatNumberPtBr(impressoes || 0) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: bruto },
            { col: 'K', valor: liquido }
          ];
        } else {
          cellsEspeciais = [
            { col: 'B', valor: especiais.objetivo || '' },
            { col: 'C', valor: especiais.formato || '' },
            { col: 'D', valor: especiais.estrategia || '' },
            { col: 'E', valor: targetValueEspeciais },
            { col: 'F', valor: especiais.interesse || '' },
            { col: 'G', valor: especiais.praca || '' },
            { col: 'H', valor: especiais.estrategia?.toLowerCase() === 'contextual native' ? formatNumberPtBr(cliques || 0) : formatNumberPtBr(impressoes || 0) },
            { col: 'I', valor: valorCompra },
            { col: 'J', valor: liquido }
          ];
        }
      } else {
        cellsEspeciais = cabecalhosEspeciais.map((cab) => {
          switch (cab.valor) {
            case 'OBJETIVO': return { col: cab.col, valor: especiais.objetivo || '' };
            case 'FORMATO': return { col: cab.col, valor: especiais.formato || '' };
            case 'ESTRATÉGIA': return { col: cab.col, valor: especiais.estrategia || '' };
            case 'TARGET': return { col: cab.col, valor: targetValueEspeciais };
            case 'INTERESSE': return { col: cab.col, valor: especiais.interesse || '' };
            case 'PRAÇA': return { col: cab.col, valor: especiais.praca || '' };
            case 'IMPRESSÕES': return { col: cab.col, valor: formatNumberPtBr(impressoes) };
            case 'BRUTO': return { col: cab.col, valor: bruto };
            case 'LÍQUIDO': return { col: cab.col, valor: liquido };
            default: return { col: cab.col, valor: '' };
          }
        });
      }

      // Definir todas as variáveis de coluna no início
      const colC = 'C';
      const colM = 'M';
      const colL = 'L';
      const colO = 'O';
      const colQ = 'Q';

      cellsEspeciais.forEach(c => {
        const cell = worksheet.getCell(`${c.col}${dataRowEspecial}`);
        cell.value = c.valor;
        Object.assign(cell, {
          ...estilos.dadosNormais,
          border: {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          }
        });

        // Aplicar formatação específica para diferentes tipos de valores
        if (["K", "L", "M"].includes(c.col)) {
          cell.numFmt = 'R$ #,##0.00';
        } else if (["J"].includes(c.col)) {
          cell.numFmt = '#,##0';
        } else if ([colO, colQ].includes(c.col)) {
          // Corrigir: se for coluna O e for VIEWS de Áudio/Spotify/Vídeo sem comissão, usar número
          if (
            c.col === 'O' &&
            formatoEspecial.toLowerCase() === 'áudio' &&
            plataformaEspecial.toLowerCase() === 'spotify' &&
            criativoNorm === 'video' &&
            !formData.comissao
          ) {
            cell.numFmt = '#,##0';
          } else {
            cell.numFmt = '0.00%';
          }
        } else if ([colM, colL].includes(c.col)) {
          cell.numFmt = '#,##0';
        }
      });

      // Adicionar linha cinza e roxa igual às programáticas principais
      const linhaAbaixoEspecial = dataRowEspecial + 3;
      
      // Determinar a coluna final para mesclagem baseado no formato e comissão
      let colEndMain;
      if ((formatoEspecial.toLowerCase() === 'áudio' && plataformaEspecial.toLowerCase() === 'spotify' && criativoNorm === 'audio')) {
        colEndMain = formData.comissao ? 'L' : 'K';
      } else if ((formatoEspecial.toLowerCase() === 'display' && plataformaEspecial.toLowerCase() === 'serasa') ||
          (formatoEspecial.toLowerCase() === 'push notification' && plataformaEspecial.toLowerCase() === 'siprocal') ||
          (formatoEspecial.toLowerCase() === 'web push' && plataformaEspecial.toLowerCase() === 'pixel roads') ||
          (formatoEspecial.toLowerCase() === 'native' && plataformaEspecial.toLowerCase() === 'tinder') ||
          (formatoEspecial.toLowerCase() === 'native' && plataformaEspecial.toLowerCase() === 'pixel roads') ||
          (formatoEspecial.toLowerCase() === 'in game' && (plataformaEspecial.toLowerCase() === 'etermax' || plataformaEspecial.toLowerCase() === 'adsmovil'))) {
        colEndMain = formData.comissao ? 'K' : 'J';
      } else if (['whatsapp', 'ctv', 'ott'].includes(formatoEspecial.toLowerCase()) ||
          (formatoEspecial.toLowerCase() === 'áudio' && plataformaEspecial.toLowerCase() === 'spotify' && criativoNorm !== 'audio') ||
          (formatoEspecial.toLowerCase() === 'push notification' && plataformaEspecial.toLowerCase() === 'hands') ||
          (formatoEspecial.toLowerCase() === 'alto impacto' && (plataformaEspecial.toLowerCase() === 'seedtag' || plataformaEspecial.toLowerCase() === 'vidoomy'))) {
        colEndMain = formData.comissao ? 'K' : 'J';
      } else {
        colEndMain = formData.comissao ? colM : colL;
      }

      // Mesclar células para a linha cinza
      worksheet.mergeCells(`${colC}${linhaAbaixoEspecial}:${colEndMain}${linhaAbaixoEspecial}`);
      const cellBrandSafety = worksheet.getCell(`${colC}${linhaAbaixoEspecial}`);
      cellBrandSafety.value = 'Brand Safety/Brand Suitability - O algoritmo de brand safety protege em 95% a marca de marketing negativo.';
      Object.assign(cellBrandSafety, {
        ...estilos.brandSafety,
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      });

      // Mesclar células para a linha roxa
      const linhaRoxaEspecial = linhaAbaixoEspecial + 1;
      worksheet.mergeCells(`B${linhaRoxaEspecial}:${colEndMain}${linhaRoxaEspecial}`);
      const cellRoxa = worksheet.getCell(`B${linhaRoxaEspecial}`);
      Object.assign(cellRoxa, estilos.cabecalhoPrincipal);

      // Só adiciona a mesclagem e formatação das colunas laterais se não for CTV, OTT, WhatsApp, Display/Serasa, Áudio/Spotify ou Alto Impacto
      if (!['ctv', 'ott', 'whatsapp'].includes(formatoEspecial.toLowerCase()) &&
          !(formatoEspecial.toLowerCase() === 'áudio' && plataformaEspecial.toLowerCase() === 'spotify') &&
          !(formatoEspecial.toLowerCase() === 'display' && plataformaEspecial.toLowerCase() === 'serasa') &&
          !(formatoEspecial.toLowerCase() === 'alto impacto' && (plataformaEspecial.toLowerCase() === 'seedtag' || plataformaEspecial.toLowerCase() === 'vidoomy'))) {
        const colStartSide = formData.comissao ? 'O' : 'N';
        const colEndSide = formData.comissao ? 'P' : 'O';
      worksheet.mergeCells(`${colStartSide}${linhaAbaixoEspecial}:${colEndSide}${linhaAbaixoEspecial}`);
      const cellBrandSafetySide = worksheet.getCell(`${colStartSide}${linhaAbaixoEspecial}`);
      cellBrandSafetySide.value = '';
      Object.assign(cellBrandSafetySide, {
        ...estilos.brandSafety,
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      });
      worksheet.mergeCells(`${colStartSide}${linhaRoxaEspecial}:${colEndSide}${linhaRoxaEspecial}`);
      const cellRoxaSide = worksheet.getCell(`${colStartSide}${linhaRoxaEspecial}`);
      Object.assign(cellRoxaSide, estilos.cabecalhoPrincipal);
      }

      // Replicar padrão de mesclagem das linhas de dados
      // B: B até B+3
      if (!worksheet.getCell(`B${dataRowEspecial}`).isMerged) {
        worksheet.mergeCells(`B${dataRowEspecial}:B${dataRowEspecial + 3}`);
      }
      // C: C até C+2
      if (!worksheet.getCell(`C${dataRowEspecial}`).isMerged) {
        worksheet.mergeCells(`C${dataRowEspecial}:C${dataRowEspecial + 2}`);
      }
      // D até L/M: cada coluna D até L/M, linha até linha+2
      const ultimaColuna = formData.comissao ? 'M' : 'L';
      for (let col = 'D'.charCodeAt(0); col <= ultimaColuna.charCodeAt(0); col++) {
        const colLetter = String.fromCharCode(col);
        if (!worksheet.getCell(`${colLetter}${dataRowEspecial}`).isMerged) {
          worksheet.mergeCells(`${colLetter}${dataRowEspecial}:${colLetter}${dataRowEspecial + 2}`);
        }
      }
      // O até Q / N até P: cada coluna, linha até linha+2
      const colStartSideEspecial = formData.comissao ? 'O' : 'N';
      const colEndSideEspecial = formData.comissao ? 'Q' : 'P';
      for (let col = colStartSideEspecial.charCodeAt(0); col <= colEndSideEspecial.charCodeAt(0); col++) {
        const colLetter = String.fromCharCode(col);
        if (!worksheet.getCell(`${colLetter}${dataRowEspecial}`).isMerged) {
          worksheet.mergeCells(`${colLetter}${dataRowEspecial}:${colLetter}${dataRowEspecial + 2}`);
        }
      }

      // Após preencher os dados das configurações especiais (WhatsApp), formatar as células monetárias como moeda
      cabecalhosEspeciais.forEach((cab) => {
        if ([tipoCompra, 'BRUTO', 'LÍQUIDO'].includes(cab.valor)) {
          const cell = worksheet.getCell(`${cab.col}${dataRowEspecial}`);
          cell.numFmt = 'R$ #,##0.00';
        }
      });
      
      // Atualizar linhaAtual para a próxima configuração
      linhaAtual = linhaRoxaEspecial + 1;
      });
    }

    // Gerar buffer da planilha
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Criar blob
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    return blob;
  } catch (error) {
    console.error('Error generating Excel file:', error);
    throw error;
  }
}; 

// Função auxiliar para formatar data
const formatarData = (data: Date | null) => {
  if (!data) return '';
  const dia = String(data.getDate()).padStart(2, '0');
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const ano = data.getFullYear();
  return `${dia}/${mes}/${ano}`;
};
